<template>
  <div
    class="datepicker-wrap"
    @keydown.enter.prevent
  >
    <Datepicker
      :disabled="disabled"
      v-model="safeModel"
      :placeholder="placeholder"
      :auto-apply="!confirm"
      :min-date="minDate"
      :max-date="maxDate"
      :hide-input-icon="iconHide"
      :locale="locale"
      :format-locale="dateFnsLocale"
      :select-text="t('select')"
      :cancel-text="t('common.cancel')"
      :enable-time-picker="enableTime"
      :format="formatProp"
      :range="range"
      :inline="inline"
      :ui="{ input: 'form-control' }"
      :multi-calendars="multi"
      :text-input="textInputConfig"
      :teleport="teleport"
      auto-position
    >
      <template
        #dp-input="{
          value,
          onInput,
          onBlur,
          onFocus,
          onEnter,
          onTab,
          onKeypress,
        }"
      >
        <div
          v-if="iconHide"
          class="dp__input_wrap dp__input_wrap--single"
        >
          <input
            :value="value"
            :placeholder="placeholder"
            class="form-control dp__input"
            :disabled="disabled"
            data-test-id="dp-input"
            inputmode="text"
            autocomplete="off"
            @input="e => handleInput(e, onInput)"
            @paste="e => handlePaste(e, onInput)"
            @blur="e => handleBlur(e, onBlur)"
            @focus="onFocus"
            @keydown.enter="onEnter"
            @keydown.tab="onTab"
            @keypress="onKeypress"
          />
        </div>
        <div
          v-else
          class="input-group w-100"
        >
          <span
            class="input-group-text dp__input_icon_decorative"
            @click.stop
          >
            <i class="bi bi-calendar"></i>
          </span>
          <input
            :value="value"
            :placeholder="placeholder"
            class="form-control dp__input"
            :disabled="disabled"
            data-test-id="dp-input"
            inputmode="text"
            autocomplete="off"
            @input="e => handleInput(e, onInput)"
            @paste="e => handlePaste(e, onInput)"
            @blur="e => handleBlur(e, onBlur)"
            @focus="onFocus"
            @keydown.enter="onEnter"
            @keydown.tab="onTab"
            @keypress="onKeypress"
          />
        </div>
      </template>
    </Datepicker>
  </div>
</template>
<script setup>
import { computed } from 'vue'
import Datepicker from '@vuepic/vue-datepicker'
import { enUS, ru } from 'date-fns/locale'
import { parseDateInput, parseDateRangeInput, formatDateForInput } from '@/lib/dateInput.js'
import { useI18n } from 'vue-i18n'

const { t, locale } = useI18n()
const dateFnsLocale = computed(() => (locale.value === 'ru' ? ru : enUS))

const props = defineProps({
  disabled: {
    type: Boolean,
    default: false,
  },
  maxDate: {
    type: [String, Date],
    default: null,
  },
  minDate: {
    type: [String, Date],
    default: null,
  },
  iconHide: {
    type: Boolean,
  },
  placeholder: String,
  enableTime: Boolean,
  range: Boolean,
  confirm: Boolean,
  inline: Boolean,
  multi: Boolean,
  teleport: Boolean,
})

const model = defineModel()

/** В range при одной дате или null во втором библиотека обращается к [1] → RangeError. Не вызываем format(null). */
const formatProp = computed(() => {
  if (!props.range) return 'dd.MM.uuuu'
  return value => {
    try {
      if (value == null) return ''
      if (Array.isArray(value)) {
        if (value.length === 1 && value[0]) {
          const s = formatDateForInput(value[0])
          return s ? `${s} - ${s}` : ''
        }
        if (value.length >= 2) {
          const s0 = value[0] ? formatDateForInput(value[0]) : ''
          const s1 = value[1] ? formatDateForInput(value[1]) : ''
          if (!s0) return ''
          return s1 ? `${s0} - ${s1}` : `${s0} - `
        }
        return value
          .map(d => (d ? formatDateForInput(d) : ''))
          .filter(Boolean)
          .join(' - ')
      }
      return value ? formatDateForInput(value) : ''
    } catch {
      return ''
    }
  }
})

function handlePaste(e, onInput) {
  onInput(e)
}

function handleInput(e, onInput) {
  onInput(e)
}

function handleBlur(e, onBlur) {
  onBlur()
}

/** Парсер ручного ввода. В range умеет "дд.мм.гггг - дд.мм.гггг". */
const textInputConfig = computed(() => ({
  format: value => (props.range ? parseDateRangeInput(value) : parseDateInput(value)),
  enterSubmit: true,
  tabSubmit: true,
}))

/** В range-режиме библиотека ожидает массив; при невалидном значении избегаем RangeError/TypeError (.map). */
function toValidDate(v) {
  if (v == null) return v
  if (v instanceof Date && !Number.isNaN(v.getTime())) return v
  if (typeof v === 'string') return parseDateInput(v) ?? null
  return null
}

function normalizeRangeValue(value) {
  if (value == null) return null
  // text-input format может вернуть одну Date или [Date, Date]
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return [value, value]
  }
  if (typeof value === 'string') {
    const parsed = parseDateRangeInput(value)
    return normalizeRangeValue(parsed)
  }
  if (!Array.isArray(value)) return null
  const d0 = value[0] instanceof Date && !Number.isNaN(value[0].getTime()) ? value[0] : toValidDate(value[0])
  const d1 = value[1] instanceof Date && !Number.isNaN(value[1]?.getTime()) ? value[1] : toValidDate(value[1])
  if (!(d0 instanceof Date)) return null
  if (!(d1 instanceof Date)) return [d0, d0]
  return [d0, d1]
}

const safeModel = computed({
  get() {
    const v = model.value
    if (props.range) {
      return normalizeRangeValue(v)
    }
    return toValidDate(v)
  },
  set(value) {
    if (props.range) {
      model.value = normalizeRangeValue(value)
      return
    }
    if (value == null || (value instanceof Date && !Number.isNaN(value.getTime()))) {
      model.value = value
    } else if (typeof value === 'string') {
      model.value = parseDateInput(value)
    } else {
      model.value = null
    }
  },
})
</script>

<i18n lang="json">
{
  "ru": {
    "select": "Выбрать"
  },
  "en": {
    "select": "Select"
  }
}
</i18n>
